import React, { useState } from "react";
import TimelineIcon from "@mui/icons-material/Timeline";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import AddSideBar from "./advertisement/Sidebar";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import "./rightPanel.css";
import { stripHiddenClasses, isLineRegion } from '../utils/colors';
const RightPanel = React.memo(
  ({
    selectedZone,
    setSelectedZone,
    zoneData,
    setActiveTabData,
    adsData,
    currentAdIndex,
    setCurrentAdIndex,
    parentActiveTab,
    drawingMode,
    setDrawingMode,
    lines,
    setLines,
    deleteLineZone,
    editLineZone,
    setActiveSlot,
    isLineZoneData,
    asTable
  }: any) => {
    const [activeTab, setActiveTab] = useState(parentActiveTab || "lines");
    const [searchTerm, setSearchTerm] = useState("");
    const [isCreateHovered, setIsCreateHovered] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedLine, setSelectedLine] = useState("");
    const [updatedLineName, setUpdatedLineName] = useState("");

    const handleTabChange = (tab: string) => {
      setActiveSlot(tab);
      setActiveTab(tab);
      setSearchTerm("");
      setActiveTabData("dashboard");
    };

    if (!zoneData) {
      return <div className="right-panel">Loading...</div>;
    }

    const getNumber = (key: string) => {
      const match = key.match(/\d+/);
      return match ? parseInt(match[0]) : 0;
    };

    const sortKeys = (obj: any) => {
      return Object.fromEntries(
        Object.entries(obj).sort(([a], [b]) => getNumber(a) - getNumber(b))
      );
    };

    // Strip UI-hidden classes from every region so the bars and the
    // max-value scaling below never see them.
    const allRegions = Object.fromEntries(
      Object.entries(zoneData?.class_counts_by_region || {}).map(
        ([region, counts]: any) => [region, stripHiddenClasses(counts, region)]
      )
    );

    const lineData = sortKeys(
      Object.fromEntries(
        Object.entries(allRegions).filter(([key]) => isLineRegion(key))
      )
    );

    const zoneOnlyData = sortKeys(
      Object.fromEntries(
        Object.entries(allRegions).filter(([key]) => !isLineRegion(key))
      )
    );

    const filteredLineData = Object.fromEntries(
      Object.entries(lineData).filter(([key]) =>
        key.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    const filteredZoneData = Object.fromEntries(
      Object.entries(zoneOnlyData).filter(([key]) =>
        key.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    const renderLoader = () => (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          padding: "24px 0px",
        }}
      >
        <CircularProgress size={22} sx={{ color: "#1e3a53" }} />
        <span style={{ fontSize: "12px", color: "#6b7280" }}>Loading...</span>
      </div>
    );

    const renderBars = (data: any) => {
      return Object.entries(data).map(([zoneKey, zoneValue]: any) => {
        const values = Object.values(zoneValue).map((v: any) => Number(v));
        const maxValue = Math.max(...values, 1);
        return (
          <div
            key={zoneKey}
            style={{
              padding: "0px 10px",
              marginBottom: "15px",
            }}
          >
            <div className={`line-title ${selectedZone === zoneKey ? "active" : ""}`}
              onClick={() => {
                setSelectedZone(
                  selectedZone === zoneKey ? "" : zoneKey
                );
              }}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                background:
                  selectedZone === zoneKey ? "#1e3a53" : "transparent",
                color:
                  selectedZone === zoneKey ? "#fff" : "#000",
                padding: "5px 0px 5px 0px",
                borderRadius: "6px",
              }}
            >
              <span>{zoneKey.toUpperCase()}</span>
              {selectedZone === zoneKey && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <EditIcon
                    sx={{
                      fontSize: 18,
                      color: "#1976d2",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();

                      setSelectedLine(zoneKey);
                      setUpdatedLineName(zoneKey);
                      setEditOpen(true);
                    }}
                  />

                  <DeleteIcon
                    sx={{
                      fontSize: 18,
                      color: "red",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteLineZone(zoneKey);
                    }}
                  />
                </div>
              )}
            </div>
            {Object.entries(zoneValue).map(([key, value]: any) => {
              const percent = (value / maxValue) * 100;
              return (
                <div key={key}>
                  <div
                    style={{
                      fontSize: "12px",
                      marginBottom: "0px",
                      display: "flex",
                      justifyContent: "space-between",
                      color: "#1e3a53",
                      fontWeight: 500,
                    }}
                  >
                    <span>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </span>
                    <span>{value}</span>
                  </div>

                  <div
                    style={{
                      background: "#e5e7eb",
                      borderRadius: "6px",
                      overflow: "hidden",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: `${percent}%`,
                        height: "20px",
                        background: "#1e3a53",
                        borderRadius: "6px",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        );
      });
    };
    // Same data as renderBars, laid out as one table: lines/zones as columns,
    // vehicle classes as rows, so lines can be compared side by side.
    const renderTable = (data: any) => {
      const regions = Object.keys(data);
      const classes: string[] = [];
      regions.forEach((r) =>
        Object.keys(data[r] || {}).forEach((c) => {
          if (!classes.includes(c)) classes.push(c);
        })
      );
      const num = (r: string, c: string) => Number(data[r]?.[c] || 0);
      const colTotal = (r: string) => classes.reduce((s, c) => s + num(r, c), 0);
      const grandTotal = regions.reduce((s, r) => s + colTotal(r), 0);
      const cell: React.CSSProperties = { padding: "6px 12px", textAlign: "right", borderBottom: "1px solid #f0f1f3" };
      const mono: React.CSSProperties = { fontFamily: "'Roboto Mono', monospace", fontSize: "13px" };

      return (
        <div style={{ overflow: "auto", maxHeight: "380px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", color: "#1e3a53" }}>
            <thead>
              <tr style={{ background: "#f3f4f6", position: "sticky", top: 0, zIndex: 1 }}>
                <th style={{ ...cell, textAlign: "left", fontWeight: 500, color: "#4b5563" }}>Class</th>
                {regions.map((r) => (
                  <th
                    key={r}
                    style={{ ...cell, fontWeight: 500 }}
                  >
                    {/* Edit/delete only for the clicked (selected) line. Their
                        space is always reserved, so nothing shifts. */}
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ display: "inline-flex", gap: "6px", visibility: selectedZone === r ? "visible" : "hidden" }}>
                          <EditIcon
                            sx={{ fontSize: 16, color: "#1976d2", cursor: "pointer" }}
                            onClick={() => {
                              setSelectedLine(r);
                              setUpdatedLineName(r);
                              setEditOpen(true);
                            }}
                          />
                          <DeleteIcon
                            sx={{ fontSize: 16, color: "red", cursor: "pointer" }}
                            onClick={() => deleteLineZone(r)}
                          />
                      </span>
                      <button
                        onClick={() => setSelectedZone(selectedZone === r ? "" : r)}
                        style={{
                          border: `1px solid ${selectedZone === r ? "#ED6926" : "#d1d5db"}`,
                          background: selectedZone === r ? "#ED6926" : "#fff",
                          color: selectedZone === r ? "#fff" : "#1e3a53",
                          borderRadius: "14px",
                          padding: "3px 12px",
                          fontSize: "12px",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        {r}
                      </button>
                    </div>
                  </th>
                ))}
                <th style={{ ...cell, fontWeight: 700 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {classes.map((c) => {
                const vals = regions.map((r) => num(r, c));
                const max = Math.max(...vals, 0);
                return (
                  <tr key={c}>
                    <td style={{ ...cell, textAlign: "left" }}>{c.charAt(0).toUpperCase() + c.slice(1)}</td>
                    {regions.map((r, i) => (
                      <td
                        key={r}
                        style={{ ...cell, background: selectedZone === r ? "#fdf1e6" : undefined }}
                      >
                        <div style={{ ...mono, fontWeight: max > 0 && vals[i] === max ? 700 : 400 }}>
                          {vals[i].toLocaleString()}
                        </div>
                        <div style={{ height: "4px", background: "#eef0f2", borderRadius: "2px", marginTop: "4px", display: "flex", justifyContent: "flex-end" }}>
                          <div style={{ height: "4px", width: `${max ? (vals[i] / max) * 100 : 0}%`, background: "#1e3a53", borderRadius: "2px" }} />
                        </div>
                      </td>
                    ))}
                    <td style={{ ...cell, ...mono, fontWeight: 500 }}>
                      {vals.reduce((s, v) => s + v, 0).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: "#fafafa" }}>
                <td style={{ ...cell, textAlign: "left", fontWeight: 700, borderTop: "1px solid #d1d5db" }}>Total</td>
                {regions.map((r) => (
                  <td key={r} style={{ ...cell, ...mono, fontWeight: 700, borderTop: "1px solid #d1d5db" }}>
                    {colTotal(r).toLocaleString()}
                  </td>
                ))}
                <td style={{ ...cell, ...mono, fontWeight: 700, borderTop: "1px solid #d1d5db" }}>
                  {grandTotal.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    };
    const renderRegions = asTable ? renderTable : renderBars;

    const isFinish =
      (drawingMode === "line" && lines?.length >= 1) ||
      (drawingMode === "zone" && lines?.length >= 2);

    const isDrawingActive =
      drawingMode === "line" || drawingMode === "zone";
    return (
      <div
        className="right-panel"
        style={asTable ? { height: "auto", minHeight: 0, background: "#fff", borderLeft: "none", overflow: "visible" } : undefined}
      >
        <div className="tabs">
          <div
            className={`tab ${activeTab === "lines" ? "active" : ""}`}
            onClick={() => handleTabChange("lines")}
          >
            <TimelineIcon fontSize="small" />
            <span>Lines</span>
          </div>

          <div
            className={`tab ${activeTab === "zones" ? "active" : ""}`}
            onClick={() => handleTabChange("zones")}
          >
            <CropSquareIcon fontSize="small" />
            <span>Zones</span>
          </div>
        </div>

        {(activeTab === "lines" || activeTab === "zones") && (
          <div
            style={{
              padding: "0px 6px 0px 6px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {filteredLineData && Object.keys(filteredLineData).length > 0 && (
              <>
                <div
                  style={{
                    flex: 1,
                    position: "relative",
                  }}
                >
                  <SearchIcon
                    style={{
                      position: "absolute",
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#888",
                      fontSize: "20px",
                    }}
                  />

                  <input
                    type="text"
                    placeholder={`Search ${activeTab}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "4px 0px 4px 35px",
                      borderRadius: "8px",
                      border: "1px solid #d1d5db",
                      outline: "none",
                      fontSize: "13px",
                      boxSizing: "border-box",
                      color: "#333",
                    }}
                  />
                </div>
                <button
                  onMouseEnter={() => setIsCreateHovered(true)}
                  onMouseLeave={() => setIsCreateHovered(false)}
                  style={{
                    background: isFinish
                      ? "#ED6926"
                      : isDrawingActive
                        ? "#ED6926"
                        : "#1e3a53",

                    color: "#fff",
                    border: isDrawingActive
                      ? "2px solid #ffb082"
                      : "1px solid transparent",

                    borderRadius: "8px",
                    padding: "2px 0px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    width: "90px",
                    fontSize: "11px",

                    transition: "all 0.2s ease",

                    transform: isCreateHovered
                      ? "translateY(-2px)"
                      : "translateY(0)",

                    boxShadow:
                      isDrawingActive || isFinish
                        ? "0 0 10px rgba(237,105,38,0.5)"
                        : isCreateHovered
                          ? "0 2px 8px rgba(0,0,0,0.2)"
                          : "none",
                  }}
                  onClick={() => {
                    if (isFinish) {
                      setDrawingMode(null);
                      setLines([]);
                      return;
                    }

                    setLines([]);

                    if (activeTab === "lines") {
                      setDrawingMode("line");
                    } else {
                      setDrawingMode("zone");
                    }
                  }}
                >
                  <AddIcon
                    fontSize="small"
                    style={{
                      marginRight: "4px",
                      transform: isDrawingActive ? "rotate(45deg)" : "rotate(0deg)",
                      transition: "0.2s ease",
                    }}
                  />

                  {isFinish
                    ? "Finish"
                    : activeTab === "lines"
                      ? "Create Line"
                      : "Create Zone"}
                </button>
              </>
            )}
          </div>
        )}
        {activeTab === "lines" && (
          isLineZoneData ? (
            renderLoader()
          ) : Object.keys(filteredLineData).length > 0 ? (
            renderRegions(filteredLineData)
          ) : (
            <div
              className="alert alert-info py-1 px-2 mx-2 mt-2 text-center"
              role="alert"
            >
              No Lines Found
            </div>
          )
        )}
        {activeTab === "zones" && (
          isLineZoneData ? (
            renderLoader()
          ) : Object.keys(filteredZoneData).length > 0 ? (
            renderRegions(filteredZoneData)
          ) : (
            <div
              className="alert alert-warning py-1 px-2 mx-2 mt-2 text-center"
              role="alert"
            >
              No Zones Found
            </div>
          )
        )}

        <Dialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Edit Line</DialogTitle>

          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Line Name"
              fullWidth
              value={updatedLineName}
              onChange={(e) => setUpdatedLineName(e.target.value)}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                editLineZone(
                  selectedLine,
                  updatedLineName.trim()
                );
                setEditOpen(false);
              }}
              sx={{ background: "#ED6926" }}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
);

export default RightPanel;

// import React, { useState } from "react";
// import TimelineIcon from "@mui/icons-material/Timeline";
// import CropSquareIcon from "@mui/icons-material/CropSquare";
// import SearchIcon from "@mui/icons-material/Search";
// import AddIcon from "@mui/icons-material/Add";
// import AddSideBar from "./advertisement/Sidebar";
// import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
// import DeleteIcon from "@mui/icons-material/Delete";
// import EditIcon from "@mui/icons-material/Edit";
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Button,
//   TextField,
// } from "@mui/material";
// import "./rightPanel.css";
// const RightPanel = React.memo(
//   ({
//     selectedZone,
//     setSelectedZone,
//     zoneData,
//     setActiveTabData,
//     adsData,
//     currentAdIndex,
//     setCurrentAdIndex,
//     parentActiveTab,
//     drawingMode,
//     setDrawingMode,
//     lines,
//     setLines,
//     deleteLineZone,
//     editLineZone,
//     setActiveSlot
//   }: any) => {
//     const [activeTab, setActiveTab] = useState(parentActiveTab || "lines");
//     const [searchTerm, setSearchTerm] = useState("");
//     const [hoveredItem, setHoveredItem] = useState("");
//     const [isCreateHovered, setIsCreateHovered] = useState(false);
//     const [editOpen, setEditOpen] = useState(false);
//     const [selectedLine, setSelectedLine] = useState("");
//     const [updatedLineName, setUpdatedLineName] = useState("");

//     const handleTabChange = (tab: string) => {
//       setActiveSlot(tab);
//       setActiveTab(tab);
//       setSearchTerm("");
//       if (tab === "advertisment") {
//         setActiveTabData("advertisment");
//         setActiveTab("advertisment");
//       } else {
//         setActiveTabData("dashboard");
//       }
//     };

//     if (!zoneData) {
//       return <div className="right-panel">Loading...</div>;
//     }

//     const getNumber = (key: string) => {
//       const match = key.match(/\d+/);
//       return match ? parseInt(match[0]) : 0;
//     };

//     const sortKeys = (obj: any) => {
//       return Object.fromEntries(
//         Object.entries(obj).sort(([a], [b]) => getNumber(a) - getNumber(b))
//       );
//     };

//     const allRegions = zoneData?.class_counts_by_region || {};

//     const lineData = sortKeys(
//       Object.fromEntries(
//         Object.entries(allRegions).filter(([key]) =>
//           key.toLowerCase().includes("line")
//         )
//       )
//     );

//     const zoneOnlyData = sortKeys(
//       Object.fromEntries(
//         Object.entries(allRegions).filter(
//           ([key]) => !key.toLowerCase().includes("line")
//         )
//       )
//     );

//     const filteredLineData = Object.fromEntries(
//       Object.entries(lineData).filter(([key]) =>
//         key.toLowerCase().includes(searchTerm.toLowerCase())
//       )
//     );

//     const filteredZoneData = Object.fromEntries(
//       Object.entries(zoneOnlyData).filter(([key]) =>
//         key.toLowerCase().includes(searchTerm.toLowerCase())
//       )
//     );

//     const renderBars = (data: any) => {
//       return Object.entries(data).map(([zoneKey, zoneValue]: any) => {
//         const values = Object.values(zoneValue).map((v: any) => Number(v));
//         const maxValue = Math.max(...values, 1);
//         return (
//           <div
//             key={zoneKey}
//             style={{
//               padding: "0px 10px",
//               marginBottom: "15px",
//             }}
//           >
//             <div className={`line-title ${selectedZone === zoneKey ? "active" : ""}`}
//               onMouseEnter={() => setHoveredItem(zoneKey)}
//               onMouseLeave={() => setHoveredItem("")}
//               onClick={() => {
//                 setSelectedZone(
//                   selectedZone === zoneKey ? "" : zoneKey
//                 );
//               }}
//               style={{
//                 display: "flex",
//                 justifyContent: "space-between",
//                 alignItems: "center",
//                 cursor: "pointer",
//                 background:
//                   selectedZone === zoneKey ? "#1e3a53" : "transparent",
//                 color:
//                   selectedZone === zoneKey ? "#fff" : "#000",
//                 padding: "5px 0px 5px 0px",
//                 borderRadius: "6px",
//               }}
//             >
//               <span>{zoneKey.toUpperCase()}</span>
//               {hoveredItem === zoneKey && (
//                 <div
//                   style={{
//                     display: "flex",
//                     alignItems: "center",
//                     gap: "8px",
//                   }}
//                 >
//                   <EditIcon
//                     sx={{
//                       fontSize: 18,
//                       color: "#1976d2",
//                       cursor: "pointer",
//                     }}
//                     onClick={(e) => {
//                       e.stopPropagation();

//                       setSelectedLine(zoneKey);
//                       setUpdatedLineName(zoneKey);
//                       setEditOpen(true);
//                     }}
//                   />

//                   <DeleteIcon
//                     sx={{
//                       fontSize: 18,
//                       color: "red",
//                       cursor: "pointer",
//                     }}
//                     onClick={(e) => {
//                       e.stopPropagation();
//                       deleteLineZone(zoneKey);
//                     }}
//                   />
//                 </div>
//               )}
//             </div>
//             {Object.entries(zoneValue).map(([key, value]: any) => {
//               const percent = (value / maxValue) * 100;
//               return (
//                 <div key={key}>
//                   <div
//                     style={{
//                       fontSize: "12px",
//                       marginBottom: "0px",
//                       display: "flex",
//                       justifyContent: "space-between",
//                       color: "#1e3a53",
//                       fontWeight: 500,
//                     }}
//                   >
//                     <span>
//                       {key.charAt(0).toUpperCase() + key.slice(1)}
//                     </span>
//                     <span>{value}</span>
//                   </div>

//                   <div
//                     style={{
//                       background: "#e5e7eb",
//                       borderRadius: "6px",
//                       overflow: "hidden",
//                       marginBottom: "8px",
//                     }}
//                   >
//                     <div
//                       style={{
//                         width: `${percent}%`,
//                         height: "20px",
//                         background: "#1e3a53",
//                         borderRadius: "6px",
//                       }}
//                     />
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         );
//       });
//     };
//     const isFinish =
//       (drawingMode === "line" && lines?.length >= 1) ||
//       (drawingMode === "zone" && lines?.length >= 2);

//     const isDrawingActive =
//       drawingMode === "line" || drawingMode === "zone";
//     return (
//       <div className="right-panel">
//         <div className="tabs">
//           <div
//             className={`tab ${activeTab === "lines" ? "active" : ""}`}
//             onClick={() => handleTabChange("lines")}
//           >
//             <TimelineIcon fontSize="small" />
//             <span>Lines</span>
//           </div>

//           <div
//             className={`tab ${activeTab === "zones" ? "active" : ""}`}
//             onClick={() => handleTabChange("zones")}
//           >
//             <CropSquareIcon fontSize="small" />
//             <span>Zones</span>
//           </div>
//           <div
//             className={`tab ${activeTab === "advertisment" ? "active" : ""
//               }`}
//             onClick={() => handleTabChange("advertisment")}
//             style={{
//               cursor: "pointer",
//             }}
//           >
//             <PlayCircleFilledIcon fontSize="small" />
//             <span>Advt</span>
//           </div>
//         </div>

//         {(activeTab === "lines" || activeTab === "zones") && (
//           <div
//             style={{
//               padding: "0px 6px 0px 6px",
//               display: "flex",
//               alignItems: "center",
//               gap: "10px",
//             }}
//           >
//             {filteredLineData && Object.keys(filteredLineData).length > 0 && (
//               <>
//                 <div
//                   style={{
//                     flex: 1,
//                     position: "relative",
//                   }}
//                 >
//                   <SearchIcon
//                     style={{
//                       position: "absolute",
//                       left: "10px",
//                       top: "50%",
//                       transform: "translateY(-50%)",
//                       color: "#888",
//                       fontSize: "20px",
//                     }}
//                   />

//                   <input
//                     type="text"
//                     placeholder={`Search ${activeTab}...`}
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                     style={{
//                       width: "100%",
//                       padding: "4px 0px 4px 35px",
//                       borderRadius: "8px",
//                       border: "1px solid #d1d5db",
//                       outline: "none",
//                       fontSize: "13px",
//                       boxSizing: "border-box",
//                       color: "#333",
//                     }}
//                   />
//                 </div>
//                 <button
//                   onMouseEnter={() => setIsCreateHovered(true)}
//                   onMouseLeave={() => setIsCreateHovered(false)}
//                   style={{
//                     background: isFinish
//                       ? "#ED6926"
//                       : isDrawingActive
//                         ? "#ED6926"
//                         : "#1e3a53",

//                     color: "#fff",
//                     border: isDrawingActive
//                       ? "2px solid #ffb082"
//                       : "1px solid transparent",

//                     borderRadius: "8px",
//                     padding: "2px 0px",
//                     cursor: "pointer",
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "center",
//                     fontWeight: 500,
//                     whiteSpace: "nowrap",
//                     width: "90px",
//                     fontSize: "11px",

//                     transition: "all 0.2s ease",

//                     transform: isCreateHovered
//                       ? "translateY(-2px)"
//                       : "translateY(0)",

//                     boxShadow:
//                       isDrawingActive || isFinish
//                         ? "0 0 10px rgba(237,105,38,0.5)"
//                         : isCreateHovered
//                           ? "0 2px 8px rgba(0,0,0,0.2)"
//                           : "none",
//                   }}
//                   onClick={() => {
//                     if (isFinish) {
//                       setDrawingMode(null);
//                       setLines([]);
//                       return;
//                     }

//                     setLines([]);

//                     if (activeTab === "lines") {
//                       setDrawingMode("line");
//                     } else {
//                       setDrawingMode("zone");
//                     }
//                   }}
//                 >
//                   <AddIcon
//                     fontSize="small"
//                     style={{
//                       marginRight: "4px",
//                       transform: isDrawingActive ? "rotate(45deg)" : "rotate(0deg)",
//                       transition: "0.2s ease",
//                     }}
//                   />

//                   {isFinish
//                     ? "Finish"
//                     : activeTab === "lines"
//                       ? "Create Line"
//                       : "Create Zone"}
//                 </button>
//               </>
//             )}
//           </div>
//         )}
//         {activeTab === "lines" && (
//           Object.keys(filteredLineData).length > 0 ? (
//             renderBars(filteredLineData)
//           ) : (
//             <div
//               className="alert alert-info py-1 px-2 mx-2 mt-2 text-center"
//               role="alert"
//             >
//               No Lines Found
//             </div>
//           )
//         )}
//         {activeTab === "zones" && (
//           Object.keys(filteredZoneData).length > 0 ? (
//             renderBars(filteredZoneData)
//           ) : (
//             <div
//               className="alert alert-warning py-1 px-2 mx-2 mt-2 text-center"
//               role="alert"
//             >
//               No Zones Found
//             </div>
//           )
//         )}
//         {activeTab === "advertisment" && (
//           <div>
//             <AddSideBar
//               ads={adsData || []}
//               currentIndex={currentAdIndex}
//               onSelectVideo={(index: number) => {
//                 setCurrentAdIndex(index);
//               }}
//             />
//           </div>
//         )}

//         <Dialog
//           open={editOpen}
//           onClose={() => setEditOpen(false)}
//           maxWidth="xs"
//           fullWidth
//         >
//           <DialogTitle>Edit Line</DialogTitle>

//           <DialogContent>
//             <TextField
//               autoFocus
//               margin="dense"
//               label="Line Name"
//               fullWidth
//               value={updatedLineName}
//               onChange={(e) => setUpdatedLineName(e.target.value)}
//             />
//           </DialogContent>

//           <DialogActions>
//             <Button onClick={() => setEditOpen(false)}>
//               Cancel
//             </Button>

//             <Button
//               variant="contained"
//               onClick={() => {
//                 editLineZone(
//                   selectedLine,
//                   updatedLineName.trim()
//                 );
//                 setEditOpen(false);
//               }}
//               sx={{ background: "#ED6926" }}
//             >
//               Update
//             </Button>
//           </DialogActions>
//         </Dialog>
//       </div>
//     );
//   }
// );

// export default RightPanel; 